// Housing service for managing neighborhood name suggestions and votes
import { app } from '../firebase';
import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  getDoc,
  getDocs, 
  updateDoc, 
  deleteDoc,
  deleteField,
  onSnapshot,
  query,
  orderBy,
  where,
  increment,
  serverTimestamp
} from 'firebase/firestore';

const db = getFirestore(app);

export interface NeighborhoodSuggestion {
  id: string;
  name: string;
  suggestedBy: string;
  createdAt: any; // Firestore timestamp
  votes: number;
  userVotes: { [userId: string]: 'up' | 'down' };
}

export interface UserVote {
  userId: string;
  suggestionId: string;
  vote: 'up' | 'down';
  createdAt: any;
}

/**
 * Submit a new neighborhood name suggestion
 */
export async function submitSuggestion(
  name: string, 
  userId: string
): Promise<string | null> {
  try {
    // Check if suggestion already exists (case insensitive)
    const existingQuery = query(
      collection(db, 'neighborhood_suggestions'),
      where('name', '==', name.toLowerCase().trim())
    );
    const existingDocs = await getDocs(existingQuery);
    
    if (!existingDocs.empty) {
      return 'A suggestion with this name already exists';
    }

    await addDoc(collection(db, 'neighborhood_suggestions'), {
      name: name.trim(),
      suggestedBy: userId,
      createdAt: serverTimestamp(),
      votes: 0,
      userVotes: {}
    });

    return null; // Success
  } catch (error) {
    console.error('Error submitting suggestion:', error);
    return 'Failed to submit suggestion';
  }
}

/**
 * Vote on a neighborhood suggestion
 */
export async function voteOnSuggestion(
  suggestionId: string,
  userId: string,
  vote: 'up'
): Promise<string | null> {
  try {
    const suggestionRef = doc(db, 'neighborhood_suggestions', suggestionId);
    
    // Get the current suggestion document directly by ID
    const suggestionDoc = await getDoc(suggestionRef);
    
    if (!suggestionDoc.exists()) {
      return 'Suggestion not found';
    }

    const suggestionData = suggestionDoc.data() as NeighborhoodSuggestion;
    const currentUserVote = suggestionData.userVotes[userId];
    
    // Calculate vote change (only 'up' votes supported now)
    let voteChange = 0;
    let finalVote: 'up' | null = vote === 'up' ? 'up' : null;
    
    if (currentUserVote === 'up' && vote === 'up') {
      voteChange = -1; // Remove upvote (unvote)
      finalVote = null; // Set to null to remove the vote
    } else if (!currentUserVote && vote === 'up') {
      voteChange = 1; // New upvote
    } else if (currentUserVote === 'down' && vote === 'up') {
      voteChange = 2; // Remove downvote, add upvote (convert existing downvote)
    }

    // Update the suggestion with new vote count and user vote
    const updateData: any = {
      votes: increment(voteChange)
    };
    
    // Handle vote removal (unvoting)
    if (finalVote === null) {
      // Use deleteField() to remove the user's vote from the userVotes object
      updateData[`userVotes.${userId}`] = deleteField();
    } else {
      // Set the user's vote
      updateData[`userVotes.${userId}`] = finalVote;
    }
    
    await updateDoc(suggestionRef, updateData);

    return null; // Success
  } catch (error) {
    console.error('Error voting on suggestion:', error);
    return 'Failed to vote on suggestion';
  }
}

/**
 * Delete a neighborhood suggestion (admin only)
 */
export async function deleteSuggestion(suggestionId: string): Promise<string | null> {
  try {
    await deleteDoc(doc(db, 'neighborhood_suggestions', suggestionId));
    return null; // Success
  } catch (error) {
    console.error('Error deleting suggestion:', error);
    return 'Failed to delete suggestion';
  }
}

/**
 * Get all neighborhood suggestions with real-time updates
 */
export function subscribeToSuggestions(
  callback: (suggestions: NeighborhoodSuggestion[]) => void
): () => void {
  // First try the composite index query
  const suggestionsQuery = query(
    collection(db, 'neighborhood_suggestions'),
    orderBy('votes', 'desc'),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(suggestionsQuery, (snapshot) => {
    const suggestions: NeighborhoodSuggestion[] = [];
    snapshot.forEach((doc) => {
      suggestions.push({
        id: doc.id,
        ...doc.data()
      } as NeighborhoodSuggestion);
    });
    callback(suggestions);
  }, (error) => {
    console.error('Error listening to suggestions:', error);
    
    // If composite index fails, try simple ordering by votes only
    if (error.message.includes('index')) {
      console.log('Falling back to simple votes ordering while index builds...');
      const fallbackQuery = query(
        collection(db, 'neighborhood_suggestions'),
        orderBy('votes', 'desc')
      );
      
      return onSnapshot(fallbackQuery, (snapshot) => {
        const suggestions: NeighborhoodSuggestion[] = [];
        snapshot.forEach((doc) => {
          suggestions.push({
            id: doc.id,
            ...doc.data()
          } as NeighborhoodSuggestion);
        });
        // Sort by createdAt on client side as fallback
        suggestions.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTime.getTime() - aTime.getTime();
        });
        callback(suggestions);
      }, (fallbackError) => {
        console.error('Fallback query also failed:', fallbackError);
        callback([]);
      });
    }
    
    callback([]);
  });
}

/**
 * Get user's vote for a specific suggestion
 */
export function getUserVote(
  suggestion: NeighborhoodSuggestion,
  userId: string
): 'up' | 'down' | null {
  return suggestion.userVotes[userId] || null;
}

/**
 * Get vote count for a suggestion
 */
export function getVoteCount(suggestion: NeighborhoodSuggestion): number {
  return suggestion.votes || 0;
}

// ============================================================================
// HOUSING PLOT PREFERENCE AND ASSIGNMENT SYSTEM
// ============================================================================

export interface PlotPreference {
  id: string;
  userId: string;
  username: string;
  characterName?: string;
  topChoices: string[]; // Array of 3 location IDs
  bottomChoices: string[]; // Array of 3 location IDs
  preferredNeighbor?: string; // Optional character/user name
  priorityLevel: number; // 1-5, where 5 = highest priority (care a lot), 1 = lowest (don't care)
  submittedAt: any; // Firestore timestamp
  assignedPlot?: string; // The plot they were assigned (if assignment has been run)
  preferenceRank?: number; // 1-3 for top choices, -1 to -3 for bottom choices, 0 for neutral
  satisfiedNeighborRequest?: boolean; // Whether their neighbor preference was satisfied
  needsManualReview?: boolean;
}

export interface PlotAssignment {
  userId: string;
  username: string;
  characterName?: string;
  assignedPlot: string;
  preferenceRank: number; // 1-3 for top choices, -1 to -3 for bottom choices, 0 for neutral
  satisfiedNeighborRequest: boolean;
  needsManualReview?: boolean;
  assignedAt: any;
}

/**
 * Submit a housing plot preference
 */
export async function submitPlotPreference(
  userId: string,
  username: string,
  characterName: string | undefined,
  topChoices: string[],
  bottomChoices: string[],
  priorityLevel: number,
  preferredNeighbor?: string
): Promise<string | null> {
  try {
    // Validate inputs
    if (topChoices.length !== 3) {
      return 'You must select exactly 3 top choices';
    }
    if (bottomChoices.length !== 3) {
      return 'You must select exactly 3 bottom choices';
    }
    
    // Check for duplicates between top and bottom
    const overlap = topChoices.filter(choice => bottomChoices.includes(choice));
    if (overlap.length > 0) {
      return 'You cannot select the same location as both a top and bottom choice';
    }

    // Check if user already has a submission
    const existingQuery = query(
      collection(db, 'housing_preferences'),
      where('userId', '==', userId)
    );
    const existingDocs = await getDocs(existingQuery);

    // Validate priority level
    if (priorityLevel < 1 || priorityLevel > 5) {
      return 'Priority level must be between 1 and 5';
    }
    
    // Prepare the data object without undefined values
    const preferenceData: any = {
      username,
      topChoices,
      bottomChoices,
      priorityLevel,
      preferredNeighbor: preferredNeighbor || null,
      submittedAt: serverTimestamp(),
    };
    
    // Only include characterName if it's provided
    if (characterName !== undefined && characterName !== null) {
      preferenceData.characterName = characterName;
    }
    
    if (!existingDocs.empty) {
      // Update existing submission
      const docId = existingDocs.docs[0].id;
      await updateDoc(doc(db, 'housing_preferences', docId), preferenceData);
    } else {
      // Create new submission
      preferenceData.userId = userId;
      await addDoc(collection(db, 'housing_preferences'), preferenceData);
    }

    return null; // Success
  } catch (error) {
    console.error('Error submitting plot preference:', error);
    return 'Failed to submit preference';
  }
}

/**
 * Get all housing plot preferences
 */
export async function getAllPlotPreferences(): Promise<PlotPreference[]> {
  try {
    const q = query(
      collection(db, 'housing_preferences'),
      orderBy('submittedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    const preferences: PlotPreference[] = [];
    snapshot.forEach((doc) => {
      preferences.push({
        id: doc.id,
        ...doc.data()
      } as PlotPreference);
    });
    
    return preferences;
  } catch (error) {
    console.error('Error fetching plot preferences:', error);
    return [];
  }
}

/**
 * Get a specific user's plot preference
 */
export async function getUserPlotPreference(userId: string): Promise<PlotPreference | null> {
  try {
    const q = query(
      collection(db, 'housing_preferences'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    } as PlotPreference;
  } catch (error) {
    console.error('Error fetching user plot preference:', error);
    return null;
  }
}

/**
 * Save plot assignments
 */
export async function savePlotAssignments(
  assignments: PlotAssignment[]
): Promise<string | null> {
  try {
    // Store assignments in a separate collection
    const batch = [];
    
    for (const assignment of assignments) {
      // Update the user's preference document with their assignment
      const prefQuery = query(
        collection(db, 'housing_preferences'),
        where('userId', '==', assignment.userId)
      );
      const prefSnapshot = await getDocs(prefQuery);
      
      if (!prefSnapshot.empty) {
        const docId = prefSnapshot.docs[0].id;
        batch.push(
          updateDoc(doc(db, 'housing_preferences', docId), {
            assignedPlot: assignment.assignedPlot,
            preferenceRank: assignment.preferenceRank,
            satisfiedNeighborRequest: assignment.satisfiedNeighborRequest,
            needsManualReview: assignment.needsManualReview || false,
          })
        );
      }
    }
    
    // Check if there's an existing active assignment
    const activeQuery = query(
      collection(db, 'housing_assignments'),
      where('isActive', '==', true)
    );
    const activeSnapshot = await getDocs(activeQuery);
    
    // Deactivate old assignments
    const deactivatePromises = activeSnapshot.docs.map(doc => 
      updateDoc(doc.ref, { isActive: false })
    );
    await Promise.all(deactivatePromises);
    
    // Clean assignments to remove undefined values (Firestore doesn't allow undefined)
    // CRITICAL: Also convert Firestore Timestamps to Date objects for proper serialization in arrays
    const cleanedAssignments = assignments.map(assignment => {
      // Convert Firestore Timestamp to Date if needed (when loading from Firestore)
      let assignedAtValue = assignment.assignedAt;
      if (assignedAtValue && typeof assignedAtValue === 'object' && 'toDate' in assignedAtValue) {
        // It's a Firestore Timestamp, convert to Date
        assignedAtValue = assignedAtValue.toDate();
      } else if (!assignedAtValue) {
        // Fallback to current date if missing
        assignedAtValue = new Date();
      }
      
      const cleaned: any = {
        userId: assignment.userId,
        username: assignment.username,
        assignedPlot: assignment.assignedPlot,
        preferenceRank: assignment.preferenceRank,
        satisfiedNeighborRequest: assignment.satisfiedNeighborRequest,
        needsManualReview: assignment.needsManualReview || false,
        assignedAt: assignedAtValue,
      };
      // Only include characterName if it's not undefined
      if (assignment.characterName !== undefined && assignment.characterName !== null) {
        cleaned.characterName = assignment.characterName;
      }
      return cleaned;
    });
    
    // Save new active assignment
    await addDoc(collection(db, 'housing_assignments'), {
      assignments: cleanedAssignments,
      isActive: true,
      createdAt: serverTimestamp(),
    });
    
    await Promise.all(batch);
    
    return null; // Success
  } catch (error) {
    console.error('Error saving plot assignments:', error);
    return 'Failed to save assignments';
  }
}

/**
 * Get the active (most recent) plot assignments
 */
export async function getActiveAssignments(): Promise<PlotAssignment[]> {
  try {
    const q = query(
      collection(db, 'housing_assignments'),
      where('isActive', '==', true)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return [];
    }
    
    // Should only be one active, but just in case take the most recent
    const docs = snapshot.docs.sort((a, b) => {
      const aTime = a.data().createdAt?.toMillis() || 0;
      const bTime = b.data().createdAt?.toMillis() || 0;
      return bTime - aTime;
    });
    
    return docs[0].data().assignments || [];
  } catch (error) {
    console.error('Error fetching active assignments:', error);
    return [];
  }
}

/**
 * Get a user's current plot assignment
 */
export async function getUserPlotAssignment(userId: string): Promise<PlotAssignment | null> {
  try {
    const assignments = await getActiveAssignments();
    return assignments.find(a => a.userId === userId) || null;
  } catch (error) {
    console.error('Error fetching user plot assignment:', error);
    return null;
  }
}

/**
 * Unassign a user from their housing plot (admin only)
 * This removes the user's assignment while preserving all other assignments
 */
export async function unassignUserFromPlot(userId: string): Promise<string | null> {
  try {
    // Load current active assignments
    const currentAssignments = await getActiveAssignments();
    
    // Check if user has an assignment
    const userAssignment = currentAssignments.find(a => a.userId === userId);
    if (!userAssignment) {
      return 'User does not have an active assignment';
    }
    
    // Remove user's assignment from the list
    const updatedAssignments = currentAssignments.filter(a => a.userId !== userId);
    
    // Update the user's preference document to remove assignment fields
    const prefQuery = query(
      collection(db, 'housing_preferences'),
      where('userId', '==', userId)
    );
    const prefSnapshot = await getDocs(prefQuery);
    
    if (!prefSnapshot.empty) {
      const docId = prefSnapshot.docs[0].id;
      await updateDoc(doc(db, 'housing_preferences', docId), {
        assignedPlot: deleteField(),
        preferenceRank: deleteField(),
        satisfiedNeighborRequest: deleteField(),
        needsManualReview: deleteField(),
      });
    }
    
    // Save updated assignments (preserving all others)
    const saveResult = await savePlotAssignments(updatedAssignments);
    if (saveResult) {
      return saveResult;
    }
    
    return null; // Success
  } catch (error) {
    console.error('Error unassigning user from plot:', error);
    return 'Failed to unassign user from plot';
  }
}

/**
 * Select an available plot for a logged-in user
 * This will ONLY work if the plot is unassigned
 * If the user already has an assignment, it will be replaced with the new one
 * All other existing assignments are preserved
 */
export async function selectPlotForUser(
  userId: string,
  username: string,
  characterName: string | undefined,
  plotNumber: string,
  plotArea: string
): Promise<string | null> {
  try {
    // CRITICAL: Load ALL current assignments first
    const currentAssignments = await getActiveAssignments();
    console.log(`[selectPlotForUser] Loaded ${currentAssignments.length} existing assignments`);
    
    const plotFullName = `${plotArea} - Plot ${plotNumber}`;
    
    // Check if this plot is already assigned to someone
    const plotAssignment = currentAssignments.find(a => a.assignedPlot === plotFullName);
    if (plotAssignment) {
      return `Plot ${plotNumber} is already assigned to ${plotAssignment.username}`;
    }
    
    // Get user's preference to calculate preference rank and get characterName
    const userPref = await getUserPlotPreference(userId);
    
    // Use characterName from preference if available, otherwise use the parameter
    const finalCharacterName = userPref?.characterName || characterName;
    
    // Helper function to calculate preference rank
    const calculatePreferenceRankForPlot = (pref: PlotPreference | null, plotFullName: string): number => {
      if (!pref) return 0; // Neutral if no preference
      
      const topIndex = pref.topChoices.indexOf(plotFullName);
      if (topIndex !== -1) return topIndex + 1;
      
      const bottomIndex = pref.bottomChoices.indexOf(plotFullName);
      if (bottomIndex !== -1) return -(bottomIndex + 1);
      
      // Check by area
      const plotMatch = plotFullName.match(/^(.+) - Plot/);
      if (plotMatch) {
        const area = plotMatch[1];
        for (let i = 0; i < pref.topChoices.length; i++) {
          const choiceMatch = pref.topChoices[i].match(/^(.+) - Plot/);
          if (choiceMatch && choiceMatch[1] === area) return i + 1;
        }
        for (let i = 0; i < pref.bottomChoices.length; i++) {
          const choiceMatch = pref.bottomChoices[i].match(/^(.+) - Plot/);
          if (choiceMatch && choiceMatch[1] === area) return -(i + 1);
        }
      }
      
      return 0; // Neutral
    };
    
    const preferenceRank = calculatePreferenceRankForPlot(userPref, plotFullName);
    
    // CRITICAL: Create updated assignments array that preserves ALL existing assignments
    // Remove user's existing assignment if they have one
    const updatedAssignments = currentAssignments.filter(a => a.userId !== userId);
    
    // Add new assignment for this user
    const newAssignment: PlotAssignment = {
      userId: userId,
      username: username,
      characterName: finalCharacterName,
      assignedPlot: plotFullName,
      preferenceRank: preferenceRank,
      satisfiedNeighborRequest: false, // Will check neighbor requests below
      needsManualReview: false,
      assignedAt: new Date(), // Use Date for arrays (not serverTimestamp)
    };
    
    // Check neighbor satisfaction
    if (userPref?.preferredNeighbor) {
      const neighborAssignment = updatedAssignments.find(a => a.username === userPref.preferredNeighbor);
      if (neighborAssignment) {
        const assignedMatch = plotFullName.match(/^(.+) - Plot/);
        const neighborMatch = neighborAssignment.assignedPlot.match(/^(.+) - Plot/);
        if (assignedMatch && neighborMatch && assignedMatch[1] === neighborMatch[1]) {
          newAssignment.satisfiedNeighborRequest = true;
        }
      }
    }
    
    // Add new assignment to the list
    updatedAssignments.push(newAssignment);
    
    // Calculate preserved count (original assignments minus the user's old one if they had one, plus the new one)
    const userHadAssignment = currentAssignments.some(a => a.userId === userId);
    const preservedCount = userHadAssignment 
      ? currentAssignments.length - 1 // Removed user's old assignment, all others preserved
      : currentAssignments.length; // All preserved, user had none
    console.log(`[selectPlotForUser] Saving ${updatedAssignments.length} assignments (preserved ${preservedCount} existing, ${userHadAssignment ? 'replaced' : 'added'} user's assignment)`);
    
    // Save updated assignments (this preserves all existing assignments)
    const saveResult = await savePlotAssignments(updatedAssignments);
    if (saveResult) {
      return saveResult;
    }
    
    return null; // Success
  } catch (error) {
    console.error('Error selecting plot for user:', error);
    return 'Failed to select plot';
  }
}

// ============================================================================
// FACTION POLL SYSTEM (Horde vs Alliance)
// ============================================================================

export interface FactionPoll {
  id: string;
  question: string;
  options: string[]; // ['Horde', 'Alliance']
  votes: { [option: string]: number }; // e.g., { 'Horde': 5, 'Alliance': 3 }
  userVotes: { [userId: string]: string }; // e.g., { 'user123': 'Horde' }
  deadline: any; // Firestore timestamp
  createdAt: any; // Firestore timestamp
  isActive: boolean;
}

/**
 * Create a faction poll (admin only)
 */
export async function createFactionPoll(
  question: string,
  options: string[],
  durationInDays: number
): Promise<string | null> {
  try {
    // Calculate deadline
    const deadline = new Date();
    deadline.setDate(deadline.getDate() + durationInDays);
    
    // Initialize votes object
    const votes: { [option: string]: number } = {};
    options.forEach(option => {
      votes[option] = 0;
    });

    await addDoc(collection(db, 'faction_polls'), {
      question,
      options,
      votes,
      userVotes: {},
      deadline,
      createdAt: serverTimestamp(),
      isActive: true
    });

    return null; // Success
  } catch (error) {
    console.error('Error creating faction poll:', error);
    return 'Failed to create poll';
  }
}

/**
 * Vote on the faction poll
 */
export async function voteOnFactionPoll(
  pollId: string,
  userId: string,
  option: string
): Promise<string | null> {
  try {
    const pollRef = doc(db, 'faction_polls', pollId);
    const pollDoc = await getDoc(pollRef);
    
    if (!pollDoc.exists()) {
      return 'Poll not found';
    }

    const pollData = pollDoc.data() as FactionPoll;
    
    // Check if poll is still active
    const deadline = pollData.deadline?.toDate ? pollData.deadline.toDate() : new Date(pollData.deadline);
    if (new Date() > deadline) {
      return 'This poll has closed';
    }

    // Check if option is valid
    if (!pollData.options.includes(option)) {
      return 'Invalid option';
    }

    const currentUserVote = pollData.userVotes[userId];
    
    // Calculate vote changes
    const updateData: any = {};
    
    if (currentUserVote === option) {
      // User is clicking the same option - remove their vote
      updateData[`votes.${option}`] = increment(-1);
      updateData[`userVotes.${userId}`] = deleteField();
    } else if (currentUserVote) {
      // User is changing their vote
      updateData[`votes.${currentUserVote}`] = increment(-1);
      updateData[`votes.${option}`] = increment(1);
      updateData[`userVotes.${userId}`] = option;
    } else {
      // User is voting for the first time
      updateData[`votes.${option}`] = increment(1);
      updateData[`userVotes.${userId}`] = option;
    }
    
    await updateDoc(pollRef, updateData);

    return null; // Success
  } catch (error) {
    console.error('Error voting on faction poll:', error);
    return 'Failed to vote on poll';
  }
}

/**
 * Get the active faction poll with real-time updates
 */
export function subscribeToActiveFactionPoll(
  callback: (poll: FactionPoll | null) => void
): () => void {
  const pollsQuery = query(
    collection(db, 'faction_polls'),
    where('isActive', '==', true),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(pollsQuery, (snapshot) => {
    if (snapshot.empty) {
      callback(null);
      return;
    }

    // Get the most recent active poll
    const doc = snapshot.docs[0];
    const poll: FactionPoll = {
      id: doc.id,
      ...doc.data()
    } as FactionPoll;

    callback(poll);
  }, (error) => {
    console.error('Error listening to faction poll:', error);
    
    // If composite index is still building, use fallback query
    if (error.message.includes('index')) {
      console.log('Index is building, using fallback query...');
      const fallbackQuery = query(
        collection(db, 'faction_polls'),
        where('isActive', '==', true)
      );
      
      return onSnapshot(fallbackQuery, (snapshot) => {
        if (snapshot.empty) {
          callback(null);
          return;
        }

        // Sort by createdAt on client side as fallback
        const polls: FactionPoll[] = [];
        snapshot.forEach((doc) => {
          polls.push({
            id: doc.id,
            ...doc.data()
          } as FactionPoll);
        });
        
        polls.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTime.getTime() - aTime.getTime();
        });
        
        callback(polls[0] || null);
      }, (fallbackError) => {
        console.error('Fallback query also failed:', fallbackError);
        callback(null);
      });
    }
    
    callback(null);
  });
}

/**
 * Get user's vote for the faction poll
 */
export function getUserFactionVote(
  poll: FactionPoll,
  userId: string
): string | null {
  return poll.userVotes[userId] || null;
}

/**
 * Check if poll is still open
 */
export function isFactionPollOpen(poll: FactionPoll): boolean {
  const deadline = poll.deadline?.toDate ? poll.deadline.toDate() : new Date(poll.deadline);
  return new Date() <= deadline;
}
